/*@flow*/
import type { Element } from "react"; /** @jsx jsx */
import { css, jsx } from "@emotion/core";
import LTCloseButton from "./LTCloseButton";
import useFilters from "./useFilters";
import useCachedQuery from "lib/useCachedQuery";
import GetMarketActivity from "./queries/GetMarketActivity";
import skipNulls from "lib/skipNulls";
import ErrorsView from "comps/ErrorsView";
import AnalyticsView from "./Analytics";
import Blank from "lib/Blank";
import Present from "lib/Present";
import { ltButtonBarBg } from "./colors";

const darkBlue = "#1f5486"; // dark blue

// Fetch the analytics data for the filters
//
function useAnalytics() {
  const v = useFilters();
  const filters = skipNulls(v).toJS();
  const variables = { filters };
  const opts = { fetchPolicy: "network-only", variables };
  return useCachedQuery(GetMarketActivity, opts);
}

function OverlaySpinner() {
  return (
    <div
      data-testid="OverlaySpinner"
      className="d-flex align-items-center justify-content-center position-absolute"
      css={css(`
        left: 0;
        top: 0;
        bottom: 0;
        right: 0;
        background: rgba(0, 0, 0, 0.1);
      `)}
    >
      <i className="fas fa-circle-notch fa-spin" />
    </div>
  );
}

function LTAnalysisEmptyView() {
  return (
    <div
      className="d-flex justify-content-center align-items-center user-select-none"
      css={css("height: 50vh;")}
    >
      No analytics for this data.
    </div>
  );
}

function LTAnalysisBodyContent() {
  const { loading, data, errors } = useAnalytics();
  if (loading) return <OverlaySpinner />;
  if (Present.is(errors)) return <ErrorsView errors={errors} />;
  if (Blank.is(data)) return <LTAnalysisEmptyView />;
  const { marketActivity = {} } = data;
  const isEmpty = Blank.is(marketActivity) || Blank.is(marketActivity.data);
  if (isEmpty) return <LTAnalysisEmptyView />;
  return <AnalyticsView data={marketActivity} />;
}

function LTAnalysisBody() {
  return (
    <div data-testid="LTAnalysisBody" className="p-4">
      <LTAnalysisBodyContent />
    </div>
  );
}

export default function LTAnalysis(): Element<string> {
  return (
    <div
      data-testid="LTAnalysis"
      css={css(`
        background-color: ${ltButtonBarBg};
        color: #333;
        min-height: 100%;
      `)}
    >
      <div className="p-4 d-flex border-bottom border-light-white justify-content-between align-items-center">
        <h5 className="text-uppercase m-0 font-weight-bold">ANALYZE</h5>
        <LTCloseButton color="#333" />
      </div>
      <LTAnalysisBody />
    </div>
  );
}
